import styled from "styled-components";
import {surfaceHeight} from "nav/SiteFooter.styles";
import {MarketingObjection} from "marketing/MarketingObjection";
import {fontFamilies, radii} from "theme/tokens";
import {authAccentInkCss, Heading} from "./LoginModal.styles";

/**
 * Externalized styles for {@link ./SignupLinkCard} (owner externalized-styles rule, 2026-07-12).
 *
 * The card is reached from an email by people who may never have opened the app, so every colour comes
 * from `theme.tokens` and both modes have to look right with no persisted preference — the same
 * constraint the unsubscribe page states, and for the same reason.
 *
 * It wears the login modal's own type (`AuthEyebrow`, {@link Heading}, `Sub`) rather than a second set
 * of headings: this IS the login page, and a reader who lands here from a reminder and a reader who
 * lands here from the nav should not be looking at two different products.
 */

/**
 * Centres the card in the viewport, over the route's aurora backdrop, and OWNS the scrolling.
 *
 * The page shell clips (`overflow: hidden`, so the aurora cannot make the page scroll sideways), which
 * means a card taller than the viewport would be cut off with no way to reach the rest of it — a
 * landscape phone, or a large accessibility text size, is enough. So this element is a fixed-height
 * scroller instead of a growing one.
 *
 * `align-items: flex-start` with `margin: auto 0` on the card, rather than `center`: a flex item
 * centred in a container it overflows has its top cut off and unreachable, while `auto` margins centre
 * it only while it fits and let it start at the top once it does not.
 */
export const Stage = styled.main`
	position: relative;
	/* The SURFACE, not the window (ui#2635). This element is what scrolls the card, and it sits inside a
	   shell that is flex: 1 0 auto because ui#2394 put a legal footer row at the foot of the app
	   scroller - so the room it actually has is the window less that row. Pinned to the window it made
	   the shell a whole viewport tall inside a scroller that is one row shorter, and the page scrolled by
	   exactly the row's height with the statutory cancellation control that far below the fold: measured
	   65px at 375x667 and 67px at 1280x800 on the reminder link's own face of /login. The declaration and
	   its pre-2022 vh fallback come from nav/SiteFooter.styles, which is where the reason for the unit is
	   written once. No backticks in these comments: one would end the template literal. */
	${surfaceHeight("height")}
	overflow-y: auto;
	-webkit-overflow-scrolling: touch;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 40px 16px;
	box-sizing: border-box;
`;

export const Card = styled.section`
	width: 430px;
	max-width: 100%;
	box-sizing: border-box;
	/* See {@link Stage}: centres the card while it fits, and lets it scroll when it does not. */
	margin: auto 0;
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.modal};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	padding: 36px 34px 32px;
	text-align: center;

	/* Focused programmatically when a press produces its outcome (see the card component), never by
	   tabbing — so it must not paint a focus ring the way a real control does. */
	&:focus {
		outline: none;
	}

	@media (max-width: 430px) {
		padding: 28px 20px 24px;
	}
`;

/**
 * The card's Instrument-Serif title, one step down from the modal's greeting because this card carries
 * a longer sentence in both languages and the Romanian title is the binding case.
 *
 * The accent is styled as an `em` ELEMENT rather than taken as a component: the title arrives from
 * `i18n/richText.*.tsx` as a whole JSX sentence, so which word is emphasised is the translator's
 * choice and this file cannot name it.
 */
export const Title = styled(Heading)`
	font-size: 32px;

	em {
		font-style: italic;
		${authAccentInkCss};
	}
`;

/** The line that reports progress or an outcome, in place of the pressable card's own lede. */
export const Status = styled.p`
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink2};
	line-height: 1.55;
	margin: 8px auto 0;
	max-width: 332px;
	/* This line carries an ADDRESS the api chose, which can be one long unbreakable token. Without
	   this it pushes the card wider than a 375px viewport. */
	overflow-wrap: anywhere;
`;

/** Holds the single action, and the gap the prototype puts above it. */
export const Action = styled.div`
	margin-top: 22px;
`;

export const FinePrint = styled.p`
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
	line-height: 1.5;
	margin: 16px 0 0;
`;

/**
 * The fine print's stop control.
 *
 * A BUTTON that looks like a link, never an anchor: pressing it posts to the api, and an anchor would
 * promise a navigation that never happens — to a screen reader, to a middle click, and to the reader
 * who copies the link address expecting a URL. `font: inherit` keeps it in the sentence it sits in.
 */
export const StopAction = styled.button`
	appearance: none;
	background: none;
	border: none;
	padding: 0;
	font: inherit;
	color: inherit;
	text-decoration: underline;
	cursor: pointer;

	&:disabled {
		cursor: default;
		opacity: 0.6;
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
		border-radius: 3px;
	}
`;

/** The api's own explanation, when it refused a stop link. */
export const Refusal = styled.p`
	font: 500 13px ${fontFamilies.sans};
	/* The api's prose, which may carry a long URL or address — same reason as {@link Status}. */
	overflow-wrap: anywhere;
	color: ${({theme}) => theme.tokens.errorInk};
	background: ${({theme}) => theme.tokens.errorTint};
	border-radius: ${radii.control};
	padding: 10px 12px;
	margin: 16px 0 0;
	line-height: 1.45;
`;

/**
 * The marketing objection box (kitecorp/transferify-ui#3503) on this centred card: its own text stays
 * left-aligned, as in the login modal's form, because a centred checkbox caption reads as a heading.
 */
export const Objection = styled(MarketingObjection)`
	&& {
		text-align: left;
	}
`;
